import {
    Box,
    Divider,
    Flex,
    Icon,
    IconButton,
    Icons,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeIconSizeVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { ExperienceViewFilterModal } from "components/pages/experience-view/experience-view-filter-modal";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import i18n from "i18next";
import { LayoutContext } from "layout/layout-context";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { checkIsExperienceViewPage, Routes } from "routes";
import {
    StepperModalModeNegativeMarginTop,
    StepperModalModePaddingTop,
    StepperModalModeStickyTop,
} from "style-variables";
import { AppMode } from "types/app-mode";
import { isGuid } from "utils";

interface ExperienceViewFilterInputProps {
    isOpenFilter: boolean;
    onOpenFilter: () => void;
    onCloseFilter: () => void;
}

function ExperienceViewFilterInput({
    isOpenFilter,
    onOpenFilter,
    onCloseFilter,
}: ExperienceViewFilterInputProps) {
    const { t } = useTranslation();
    const { guests, guestsChildren, date, isActivityBooking, isWaitList } =
        useBookingStateContext();
    const { clearGuestDateState } = useBookingActionsContext();
    const { appMode } = useContext(LayoutContext);
    const { data: booking } = useWebBooking();
    const externalBookingSettings = useExternalBookingSettings().data;
    const { activateWebChildren } = externalBookingSettings?.unitsMetaData ?? {};

    const navigate = useNavigate();

    const stickyInputFilter = appMode === AppMode.Modal || appMode === AppMode.Standalone;
    const useModalModePositioning = appMode === AppMode.Modal;

    let summaryText = "";
    if (guests) {
        if (!activateWebChildren || !guestsChildren) {
            summaryText = guests.toString() + " " + t("experienceView.guests").toLocaleLowerCase();
        } else {
            const adults = guests - guestsChildren;
            summaryText = adults.toString() + " " + t("adults");
            summaryText += ", " + guestsChildren + " " + t("children");
        }
    }
    const dateText = date?.locale(i18n.language).format("D MMM") ?? "";
    if (summaryText && dateText) {
        summaryText += `, ${dateText}`;
    } else if (dateText) {
        summaryText = dateText;
    }

    const placeholder = t("experienceView.selectGuestsAndDate");
    const disableInputField = !checkIsExperienceViewPage(location.pathname);

    const removeBookingGuidFromUrl = () => {
        if (!booking?.guid) return;
        const newPath = location.pathname.replace(`/${booking.guid}`, "");
        void navigate(`${newPath}${location.search}`, { replace: true });
    };

    const handleGoBack = () => {
        const newSegments = location.pathname
            .replace(Routes.FINALIZE, Routes.EXPERIENCE_VIEW)
            .split("/");

        // Remove the booking guid segment that follows experience-view (if present) for simple booking or waitlist.
        const lastSegment = newSegments[newSegments.length - 1];
        if ((!isActivityBooking || isWaitList) && isGuid(lastSegment)) {
            newSegments.pop();
        }

        void navigate(`${newSegments.join("/")}${location.search}`, { replace: false });
    };

    const handleInputClick = () => {
        if (booking) {
            removeBookingGuidFromUrl();
        } else {
            onOpenFilter();
        }
    };

    const inputBgColor = disableInputField
        ? ThemeColorVariable.SurfaceDisabled
        : ThemeColorVariable.Background;
    const inputBorderColor = disableInputField
        ? "transparent"
        : ThemeColorVariable.OnBackgroundBorder;
    return (
        <Stack
            spacing={ThemeSpaceVariable.None}
            mt={useModalModePositioning ? StepperModalModeNegativeMarginTop : 0}
            pt={useModalModePositioning ? StepperModalModePaddingTop : ThemeSpaceVariable.Medium}
            position={stickyInputFilter ? "sticky" : "static"}
            top={useModalModePositioning ? StepperModalModeStickyTop : 0}
            bg={ThemeColorVariable.Background}
            zIndex={2}
            mb={ThemeSpaceVariable.Medium}
        >
            <Flex
                align="center"
                flex={1}
                gap={ThemeSpaceVariable.Small}
                px={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Medium}
            >
                {disableInputField && (
                    <IconButton
                        aria-label={t("back")}
                        icon={Icons.Back}
                        variant="ghost"
                        onClick={handleGoBack}
                    />
                )}
                <Box
                    as="button"
                    display="flex"
                    alignItems="center"
                    w="full"
                    gap={ThemeSpaceVariable.Small}
                    px={ThemeSpaceVariable.Medium}
                    py={ThemeSpaceVariable.Small}
                    h="48px"
                    border={"1px solid"}
                    borderColor={inputBorderColor}
                    borderRadius={ThemeRadiusVariable.XLarge}
                    bg={inputBgColor}
                    cursor={disableInputField ? "auto" : "pointer"}
                    onClick={disableInputField ? undefined : handleInputClick}
                    aria-label={t("experienceView.selectGuestsAndDate")}
                    aria-disabled={disableInputField}
                    // @ts-expect-error // ! TODO: Include hover prop in ThemeStyleProps for box in casper-ui-library: Props are spread to Box which supports _hover, but TypeScript does not recognize it due to missing type definitions in casper-ui-library
                    _hover={
                        disableInputField ? {} : { borderColor: ThemeColorVariable.OnBackground }
                    }
                    _disabled={{ borderColor: inputBorderColor, bg: inputBgColor }}
                >
                    <Icon
                        icon={Icons.Search}
                        size={ThemeIconSizeVariable.Medium}
                        color={
                            disableInputField
                                ? ThemeColorVariable.OnSurfaceSubdued
                                : ThemeColorVariable.OnBackground
                        }
                    />
                    <Text
                        flex={1}
                        fontSize={ThemeFontSizeVariable.Medium}
                        fontWeight={summaryText ? "medium" : "normal"}
                        color={
                            summaryText && !disableInputField
                                ? ThemeColorVariable.OnBackground
                                : ThemeColorVariable.OnBackgroundSubdued
                        }
                    >
                        {summaryText || `${placeholder} ...`}
                    </Text>

                    {guests && (
                        <Box
                            as="span"
                            role="button"
                            tabIndex={0}
                            aria-label={t("clear")}
                            pointerEvents={disableInputField ? "none" : "auto"}
                            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                                if (booking) {
                                    removeBookingGuidFromUrl();
                                } else {
                                    clearGuestDateState?.();
                                }
                                e.stopPropagation();
                            }}
                        >
                            <Icon
                                icon={Icons.Clear}
                                size={ThemeIconSizeVariable.Medium}
                                color={
                                    disableInputField
                                        ? ThemeColorVariable.OnSurfaceSubdued
                                        : ThemeColorVariable.OnBackground
                                }
                            />
                        </Box>
                    )}
                </Box>

                <ExperienceViewFilterModal isOpen={isOpenFilter} onClose={onCloseFilter} />
            </Flex>
            {/* @ts-expect-error // ! TODO: Add sx prop to Box TypeScript interface in casper-ui-library */}
            <Box sx={{ display: "block", "@media (min-width: 560px)": { display: "none" } }}>
                <Divider />
            </Box>
        </Stack>
    );
}

export { ExperienceViewFilterInput };
